import React from "react";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useTheme } from "@mui/material";
import { getSideDrawerStyles } from "@components/SideDrawer/SideDrawer.styles";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useDownloadCertificate } from "@pages/vessel-management/vessel-status/hooks/useDownloadCertificate";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { translateNormalizedRole } from "@utils/roles";
import { checkPermission } from "@utils/user-permissions";
import { useTranslation } from "react-i18next";
import { formatDateTime } from "./utils";

const DEFAULT_ATTACHMENT_NAME = "attachment";

interface StatusSideDrawerProps {
  isOpen: boolean;
  onClose: (drawerName: string) => void;
  submittedBy?: string | null;
  submittedByRole?: string | null;
  createdAt?: string | null;
  externalNotes?: string | null;
  rejectionReason?: string | null;
  internalNotes?: string | null;
  externalNotesDocument?: string | null;
  externalNotesDocumentName?: string | null;
  rejectionAttachmentsUrl?: string | null;
  rejectionAttachmentsName?: string | null;
  internalNotesDocument?: string | null;
  internalNotesDocumentName?: string | null;
  cancelationReason?: string | null;
  cancelationAttachments?: { url: string; fileName: string }[] | null;
}

const serviceVesselCall = "bff-vessel-call";
const StatusSideDrawer: React.FC<StatusSideDrawerProps> = ({
  isOpen,
  onClose,
  submittedBy,
  submittedByRole,
  createdAt,
  externalNotes,
  internalNotes,
  externalNotesDocument,
  externalNotesDocumentName,
  internalNotesDocument,
  internalNotesDocumentName,
  rejectionReason,
  rejectionAttachmentsUrl,
  rejectionAttachmentsName,
  cancelationReason,
  cancelationAttachments,
}) => {
  const { t } = useTranslation();
  const { userAccessPermissions } = useAuthStore();
  const isMarineDepartment = checkPermission(userAccessPermissions, "hasChangeVesselStatusAccess");
  const { handleDownload, handlePreview } = useDownloadCertificate();
  const theme = useTheme();
  const styles = getSideDrawerStyles(theme);
  const handleSubmit = () => {
    onClose("statusDrawer");
  };
  const resolvedExternalNotesDocumentName =
    externalNotesDocumentName?.trim() || DEFAULT_ATTACHMENT_NAME;
  const resolvedInternalNotesDocumentName =
    internalNotesDocumentName?.trim() || DEFAULT_ATTACHMENT_NAME;
  const resolvedRejectionAttachmentsName =
    rejectionAttachmentsName?.trim() || DEFAULT_ATTACHMENT_NAME;
  const submittedByName = submittedBy;
  const submittedByRoleText = translateNormalizedRole(t, submittedByRole);
  const hasSubmittedByInfo = Boolean(submittedByName || submittedByRoleText);
  const createdAtText = createdAt ? formatDateTime(createdAt) : "-";

  return (
    <div data-testid="status-sidedrawer" className="min-w-[400px]">
      <SideDrawer
        isOpen={isOpen}
        title={t("vessel-call.details")}
        onClose={() => onClose("statusDrawer")}
        submitLabel={t("common.ok")}
        onSubmit={handleSubmit}
        hideCancelButton={true}
      >
        <div className="w-[400px] p-6">
          {/* EXTERNAL NOTES */}
          {externalNotes && (
            <>
              <div className="text-sm font-bold mb-1" style={styles.descriptionText}>
                {t("vessel-call.external-notes")}
              </div>
              <p className="text-sm mb-2" style={styles.descriptionText}>
                {externalNotes}
              </p>
            </>
          )}
          {externalNotesDocument && (
            <>
              <div className="text-sm text-gray-500 font-semibold mb-1">
                {t("vessel-call.external-attachments")}
              </div>
              <div className="border rounded p-3 w-full border-[#F1F2F4] mb-4">
                <div className="flex justify-between items-center w-full">
                  <a
                    href={externalNotesDocument}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline break-all cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      handlePreview("document", serviceVesselCall, externalNotesDocument);
                    }}
                  >
                    {resolvedExternalNotesDocumentName}
                  </a>
                  <button
                    type="button"
                    onClick={() =>
                      externalNotesDocument &&
                      resolvedExternalNotesDocumentName &&
                      handleDownload(
                        "document",
                        serviceVesselCall,
                        externalNotesDocument,
                        resolvedExternalNotesDocumentName,
                      )
                    }
                    className="cursor-pointer"
                    title={"Download"}
                  >
                    <FileDownloadOutlinedIcon />
                  </button>
                </div>
              </div>
            </>
          )}
          {/* REJECTION REASON */}
          {rejectionReason && (
            <>
              <div className="text-sm font-bold mb-1" style={styles.descriptionText}>
                {t("vessel-call.rejection-reason")}
              </div>
              <p className="text-sm mb-2" style={styles.descriptionText}>
                {rejectionReason}
              </p>
            </>
          )}
          {rejectionAttachmentsUrl && (
            <>
              <div className="text-sm text-gray-500  font-semibold mb-1">
                {t("vessel-call.rejection-attachments")}
              </div>
              <div className="border rounded p-3 w-full border-[#F1F2F4]">
                <div className="flex justify-between items-center w-full">
                  <a
                    href={rejectionAttachmentsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline break-all cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      handlePreview("document", serviceVesselCall, rejectionAttachmentsUrl);
                    }}
                  >
                    {resolvedRejectionAttachmentsName}
                  </a>
                  <button
                    type="button"
                    onClick={() =>
                      rejectionAttachmentsUrl &&
                      resolvedRejectionAttachmentsName &&
                      handleDownload(
                        "document",
                        serviceVesselCall,
                        rejectionAttachmentsUrl,
                        resolvedRejectionAttachmentsName,
                      )
                    }
                    className="cursor-pointer"
                    title={"Download"}
                  >
                    <FileDownloadOutlinedIcon />
                  </button>
                </div>
              </div>
            </>
          )}
          {/* CANCELLATION REASON */}
          {cancelationReason && (
            <>
              <div className="text-sm font-bold mb-1" style={styles.descriptionText}>
                {t("vessel-call.cancellation-reason")}
              </div>
              <p className="text-sm mb-2" style={styles.descriptionText}>
                {cancelationReason}
              </p>
            </>
          )}
          {Boolean(cancelationAttachments?.length) && (
            <>
              <div className="text-sm text-gray-500 font-semibold mb-1">
                {t("vessel-call.cancellation-attachments")}
              </div>
              {cancelationAttachments?.map(({ url, fileName }, index) => {
                const name = fileName.trim() || DEFAULT_ATTACHMENT_NAME;
                return (
                  <div
                    className="border rounded p-3 w-full border-[#F1F2F4] mb-2"
                    key={`${url}-${index}`}
                  >
                    <div className="flex justify-between items-center w-full">
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline break-all cursor-pointer"
                        onClick={(e) => {
                          e.preventDefault();
                          handlePreview("document", serviceVesselCall, url);
                        }}
                      >
                        {name}
                      </a>
                      <button
                        type="button"
                        onClick={() => handleDownload("document", serviceVesselCall, url, name)}
                        className="cursor-pointer"
                        title="Download"
                      >
                        <FileDownloadOutlinedIcon />
                      </button>
                    </div>
                  </div>
                );
              })}
            </>
          )}
          {/* INTERNAL NOTES */}
          {isMarineDepartment && internalNotes && (
            <>
              <div className="text-sm font-semibold mb-1" style={styles.descriptionText}>
                {t("vessel-call.internal-notes")}
              </div>
              <p className="text-sm mb-2" style={styles.descriptionText}>
                {internalNotes}
              </p>
            </>
          )}
          {isMarineDepartment && internalNotesDocument && (
            <>
              <div className="text-sm text-gray-500  font-semibold mb-1">
                {t("vessel-call.internal-attachments")}
              </div>
              <div className="border rounded p-3 w-full border-[#F1F2F4]">
                <div className="flex justify-between items-center w-full">
                  <a
                    href={internalNotesDocument}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline break-all cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      handlePreview("document", serviceVesselCall, internalNotesDocument);
                    }}
                  >
                    {resolvedInternalNotesDocumentName}
                  </a>
                  <button
                    type="button"
                    onClick={() =>
                      internalNotesDocument &&
                      resolvedInternalNotesDocumentName &&
                      handleDownload(
                        "document",
                        serviceVesselCall,
                        internalNotesDocument,
                        resolvedInternalNotesDocumentName,
                      )
                    }
                    className="cursor-pointer"
                    title={"Download"}
                  >
                    <FileDownloadOutlinedIcon />
                  </button>
                </div>
              </div>
            </>
          )}
          <div className="mt-4">
            <p className="text-xs text-gray-500">
              <span>{t("vessel-call.interruption.submittedBy")}:</span>{" "}
              {hasSubmittedByInfo ? (
                <>
                  {submittedByName && <span className="text-gray-800">{submittedByName}</span>}
                  {submittedByName && submittedByRoleText ? " / " : null}
                  {submittedByRoleText}
                </>
              ) : (
                "-"
              )}
            </p>
            <p className="text-xs text-gray-500 mt-1">{createdAtText}</p>
          </div>
        </div>
      </SideDrawer>
    </div>
  );
};

export default StatusSideDrawer;
